import { faCamera } from '@fortawesome/free-solid-svg-icons/faCamera';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import isString from 'lodash-es/isString';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { atiraLinkTestIDs } from '../constants/test/atiralink.testIds';
import { Rounded } from '../theme/Rounded';
import { Spacing } from '../theme/Spacing';
import { getMediaUrl } from '../utils/Media';
import {
  Message,
  Tooltip,
  Upload,
  UploadChangeParam,
  UploadFile,
  UploadProps,
} from '../utils/local-antd';
import { AtiraIcon } from './AtiraIcon';
import { Button } from './Button';
import { Flex } from './Flex';
import { ImageCreateEdit } from './ImageCreateEdit';
import { Text } from './Text';

type UploadListType = 'text' | 'picture' | 'picture-card' | 'picture-circle';

const StyledTitle = styled(Text)`
  font-size: initial;
`;

const RequiredSpan = styled.span`
  color: ${({ theme }) => theme.danger};
`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.subTextColor};
`;

const UploadWrapper = styled(Flex)<{
  listType: UploadListType;
  isFileUploaded: boolean;
}>`
  justify-content: center;

  .ant-upload-select {
    width: 100%;
    display: block;
  }

  .ant-image-img {
    height: ${({ listType }) =>
      listType === 'picture-circle' ? '100%' : 'auto'};
    object-fit: cover;
    aspect-ratio: ${({ listType }) =>
      listType === 'picture-circle' ? '1 / 1' : 'auto'};
  }

  ${({ listType, theme, isFileUploaded }) =>
    listType === 'picture'
      ? `
      .ant-upload-wrapper {
        border-radius: ${Rounded.md};
        cursor: pointer;
      }

      .ant-upload-select .ant-upload {
        padding: ${!isFileUploaded ? Spacing.l : 0};
        display: block;
        overflow: hidden;
        border-radius: ${Rounded.md};
      }

    `
      : ''}
`;

const ChangeImageWrapper = styled(Flex)`
  .ant-upload-wrapper {
    border-color: transparent !important;
  }

  .ant-upload-select {
    width: 2rem !important;
    height: 2rem !important;
    padding: calc(${Spacing.s} / 2);
    background-color: ${({ theme }) => theme.main} !important;
    transition: all 0.3s !important;
    border-radius: ${Rounded.circle} !important;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ant-upload-select:hover {
    background-color: ${({ theme }) => theme.mainHover} !important;
  }
`;

const RemoveButton = styled(Button)`
  border-radius: ${Rounded.circle};
  padding: calc(${Spacing.s} / 2);
  width: 2rem !important;
  height: 2rem !important;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const uploadProps: UploadProps = {
  name: 'file',
  defaultFileList: [],
  beforeUpload: (file) => false,
  onChange(info) {
    if (info.file.status === 'done') {
      Message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === 'error') {
      Message.error(`${info.file.name} file upload failed.`);
    }
  },
};

type ImageUploadProps = {
  onChange: ATVoidFunction<UploadFile | null>;
  defaultFiles?: UploadFile[];
  listType?: UploadListType;
  showUploadList?: boolean;
  title?: string;
  required?: boolean;
  errorMessage?: string;
  valid?: boolean;
  image: string;
  dataTestId?: string;
  showAspectRatioTooltip?: boolean;
} & UploadProps;

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onChange,
  defaultFiles,
  listType = 'picture-circle',
  showUploadList = false,
  required = false,
  valid = true,
  title,
  errorMessage,
  image,
  dataTestId,
  showAspectRatioTooltip = true,
  ...props
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const { t } = useTranslation();

  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    uploadProps.onChange!(info);
    if (info.file.status !== 'removed') {
      onChange(info.file);

      setFileList([info.file]);
    }
  };

  const handleRemove = () => {
    onChange(null);
    setFileList([]);
  };

  useEffect(() => {
    if (image && isString(image)) {
      const imageUrl = getMediaUrl(image);

      if (imageUrl) {
        setFileList([
          {
            uid: image,
            name: 'image.jpg',
            status: 'done',
            url: imageUrl,
          },
        ]);
      }
    }
  }, [image]);

  return (
    <Flex flexDirection="column">
      {title ? (
        <StyledTitle>
          {title}
          {required ? (
            <RequiredSpan> *</RequiredSpan>
          ) : (
            <OptionalSpan> ({t('common.optional')})</OptionalSpan>
          )}
        </StyledTitle>
      ) : null}

      <UploadWrapper listType={listType} isFileUploaded={fileList.length > 0}>
        <Tooltip
          title={
            showAspectRatioTooltip
              ? `${t('common.click_upload')}. ${t(
                  'atira_link.create.content.image.upload.recomended.ratio',
                )}`
              : ''
          }
        >
          <Upload
            showUploadList={showUploadList}
            listType={listType}
            {...uploadProps}
            maxCount={1}
            defaultFileList={fileList}
            fileList={fileList}
            onChange={handleChange}
            {...props}
          >
            {fileList.length ? (
              <ImageCreateEdit src={image} />
            ) : (
              <AtiraIcon icon={faCamera} size="2x" color="main" />
            )}
          </Upload>
        </Tooltip>
      </UploadWrapper>

      <Flex gap="s" justifyContent="center" marginTop="s">
        <ChangeImageWrapper>
          <Tooltip
            title={
              showAspectRatioTooltip
                ? `${t('common.click_upload')}. ${t(
                    'atira_link.create.content.image.upload.recomended.ratio',
                  )}`
                : ''
            }
          >
            <Upload
              showUploadList={showUploadList}
              listType={listType}
              {...uploadProps}
              maxCount={1}
              defaultFileList={defaultFiles}
              onChange={handleChange}
              className={dataTestId}
              {...props}
            >
              <AtiraIcon icon={faEdit} color="sub" />
            </Upload>
          </Tooltip>
        </ChangeImageWrapper>

        <Tooltip title={t('common.remove')}>
          <RemoveButton
            icon={faTrash}
            onClick={handleRemove}
            iconWidth="xs"
            dataTestId={atiraLinkTestIDs.content.imageRemove}
            disabled={!fileList.length}
          />
        </Tooltip>
      </Flex>
      {errorMessage && !valid ? (
        <Text color="danger">{errorMessage}</Text>
      ) : null}
    </Flex>
  );
};
